/**
 * Sticky add-to-cart bar (sections/float-add-to-cart.liquid).
 *
 * The bar is a mirror of the main product form and keeps no state of its own:
 * - an option click selects the same option value in the main form;
 * - the add-to-cart click is forwarded to the main form's submit button;
 * - checked options, availability, price and button state are copied from the main form.
 *
 * Main form elements are always looked up by id, because the main section
 * re-renders parts of itself on every variant change.
 */
if (!customElements.get('float-add-to-cart')) {
  customElements.define(
    'float-add-to-cart',
    class FloatAddToCart extends HTMLElement {
      static FALLBACK_HEIGHT = 168;

      connectedCallback() {
        const mainInfo = this.findMainProductInfo();
        if (!mainInfo) return;

        this.mainSectionId = mainInfo.dataset.section;
        this.submitButton = this.querySelector('[data-float-submit]');
        this.submitButtonText = this.querySelector('[data-float-submit-text]');

        this.onClick = this.onClick.bind(this);
        this.addEventListener('click', this.onClick);

        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, () => this.syncFromMain());
        this.buttonObserver = new MutationObserver(() => this.syncButton());

        this.syncFromMain();
        this.initVisibility();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.unsubscribeVariantChange?.();
        this.buttonObserver?.disconnect();
        this.visibilityObserver?.disconnect();
      }

      /* ---------- main form lookups ---------- */

      findMainProductInfo() {
        return Array.from(document.querySelectorAll('product-info[id^="MainProduct-"]')).find(
          (element) => !element.closest('quick-add-modal')
        );
      }

      get mainForm() {
        return document.getElementById(`product-form-${this.mainSectionId}`);
      }

      get mainProductForm() {
        return this.mainForm?.closest('product-form') ?? null;
      }

      get mainSubmitButton() {
        return document.getElementById(`ProductSubmitButton-${this.mainSectionId}`);
      }

      get mainVariantSelects() {
        return document.getElementById(`variant-selects-${this.mainSectionId}`);
      }

      get mainPrice() {
        return document.getElementById(`price-${this.mainSectionId}`)?.querySelector('.price') ?? null;
      }

      getMainOption(optionValueId) {
        return this.mainVariantSelects?.querySelector(`[data-option-value-id="${optionValueId}"]`) ?? null;
      }

      /* ---------- user actions: forwarded to the main form ---------- */

      onClick(event) {
        if (event.target.closest('[data-float-submit]')) {
          event.preventDefault();
          this.mainSubmitButton?.click();
          return;
        }

        const label = event.target.closest('label');
        const input = label ? label.control : event.target.closest('input[data-option-value-id]');
        if (!input || !input.dataset.optionValueId || !this.contains(input)) return;

        // The bar's own inputs never change natively; the main form is the source of truth.
        event.preventDefault();
        event.stopPropagation();
        this.selectInMain(input);
      }

      selectInMain(input) {
        const mainOption = this.getMainOption(input.dataset.optionValueId);
        if (!mainOption) return;

        if (mainOption.tagName === 'OPTION') {
          const select = mainOption.closest('select');
          if (select.value === mainOption.value) return;
          select.value = mainOption.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          if (mainOption.checked) return;
          mainOption.checked = true;
          mainOption.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Optimistic UI until the main form finishes its update and syncFromMain runs.
        input.checked = true;
        this.syncSelectedLabels();
      }

      /* ---------- state: copied from the main form ---------- */

      syncFromMain() {
        this.syncOptions();
        this.syncPrice();
        this.observeMainButton();
        this.syncButton();
      }

      syncOptions() {
        if (!this.mainVariantSelects) return;

        this.querySelectorAll('input[data-option-value-id]').forEach((input) => {
          const mainOption = this.getMainOption(input.dataset.optionValueId);
          if (!mainOption) return;

          input.checked = mainOption.tagName === 'OPTION' ? mainOption.selected : mainOption.checked;

          const unavailable =
            mainOption.classList.contains('disabled') || mainOption.classList.contains('visually-disabled');
          const unavailableClass = input.classList.contains('swatch-input__input') ? 'visually-disabled' : 'disabled';
          input.classList.toggle(unavailableClass, unavailable);
        });

        this.syncSelectedLabels();
      }

      syncSelectedLabels() {
        this.querySelectorAll('fieldset').forEach((fieldset) => {
          const selectedValue = fieldset.querySelector(':scope > legend [data-selected-value]');
          const checked = fieldset.querySelector('input:checked');
          if (selectedValue && checked) selectedValue.textContent = checked.value;
        });
      }

      syncPrice() {
        const source = this.mainPrice;
        const target = this.querySelector('[data-float-price] > .price');
        if (!source || !target) return;

        const price = source.cloneNode(true);
        price.classList.remove('price--large', 'price--show-badge');
        target.replaceWith(price);
      }

      observeMainButton() {
        const productForm = this.mainProductForm;
        if (!productForm || productForm === this.observedProductForm) return;

        this.buttonObserver.disconnect();
        this.buttonObserver.observe(productForm, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
        });
        this.observedProductForm = productForm;
      }

      syncButton() {
        const source = this.mainSubmitButton;
        if (!source || !this.submitButton) return;

        this.submitButton.toggleAttribute('disabled', source.hasAttribute('disabled'));
        if (source.getAttribute('aria-disabled') === 'true') {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
        }
        this.submitButton.classList.toggle('loading', source.classList.contains('loading'));

        const sourceSpinner = source.querySelector('.loading__spinner');
        const spinner = this.submitButton.querySelector('.loading__spinner');
        if (sourceSpinner && spinner) spinner.classList.toggle('hidden', sourceSpinner.classList.contains('hidden'));

        const sourceText = (this.mainProductForm?.submitButtonText ?? source.querySelector('span'))?.textContent.trim();
        if (sourceText && this.submitButtonText && this.submitButtonText.textContent.trim() !== sourceText) {
          this.submitButtonText.textContent = sourceText;
        }
      }

      /* ---------- visibility (behaviour kept from the previous version) ---------- */

      initVisibility() {
        const target =
          document.querySelector('.product__info-container > div > .product-form') ?? this.mainProductForm;
        if (!target) return;

        const targetTop = target.getBoundingClientRect().top;

        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
              this.setVisible(scrollTop <= targetTop && !entry.isIntersecting);
            });
          },
          { root: null, threshold: 0 }
        );
        this.visibilityObserver.observe(target);
      }

      setVisible(visible) {
        this.classList.toggle('active', visible);

        const offset = visible ? this.offsetHeight || FloatAddToCart.FALLBACK_HEIGHT : 0;
        [
          document.querySelector('.float_whatsapp'),
          document.querySelector('body .smart-accessibility-widget .sa-widget__cta.bottom-left'),
        ].forEach((icon) => {
          if (icon) icon.style.transform = `translateY(-${offset}px)`;
        });
      }
    }
  );
}
